<template>
  <div class="page page-efriends content">
    <div class="page-efriends__top">
      <div class="page-efriends__top-left">
        <h1 class="page__title">{{ t('efriends.title') }}</h1>


        <div class="page-efriends__energy-lines" v-if="!isCommunityless">
          <p v-if="referralLink">
            Deine Friends sind noch keine eFriends?
            <br>
            <LinkButton
              :href="'https://api.whatsapp.com/send?text=' + encodeURIComponent('Hey 👋, ich bin bei eFriends – da teilen wir Strom direkt miteinander ⚡️. Wenn du mitmachst, bekommst du 30 € Startbonus (und ich auch 🎉).\nHier gehts zur Anmeldung: ' + referralLink)"
              :text="'Freunde einladen via WhatsApp'"
              icon-left="WhatsApp"
              :extra-classes="['button--whatsapp']"
              target="_blank"
              :icon-classes="[]"
            />
            <br>
            Dein neuer eFriend erhält 30€ Rabatt auf die eFriends WATCH+ und du bekommst eine Gutschrift im Wert von 30€.
          </p>
          <template v-if="unusedEnergyLines > 0">
            Du hast noch
            <span>
              {{ unusedEnergyLines }} freie Energyline{{unusedEnergyLines > 1 ? 's' : ''}}.
            </span
            >
          </template>
          <template v-else> Du hast alle Energylines in Verwendung. </template>

          <template v-if="hasProduction && openConnectionsAvailable > -1">
            <br />Von deiner Anlage
            <span>{{
              openConnectionsAvailable === 1
                ? `kann noch 1 ${eFriendsSingular}`
                : `können noch ${openConnectionsAvailable} ${eFriendsPlural}`
            }}</span>
            beziehen.
          </template>
          <template v-if="likesGiven !== null"
          ><br />
            <template v-if="hasProduction">
              Du hast dieses Monat bisher <span>{{ likesGiven }} Likes</span>.<br />
            </template>
            Du kannst dieses Monat noch
            <span>{{ availableLikes }} Likes</span> vergeben.
          </template>
        </div>

        <div class="page-efriends__energy-lines" v-else v-html="t('global.communitylessFriendsHintLong')">
        </div>
      </div>

      <ListAndMapPreview />
    </div>


    <template v-if="!friendsLoaded">
      <Spinner />
    </template>
    <template v-else>
      <Contributors v-if="contributors.length > 0" />
      <Donatees v-if="donatees.length > 0" />
      <Producers v-if="hasConsumption" />
      <Consumers v-if="hasProduction" />
    </template>
  </div>
</template>

<script>
  import ListAndMapPreview from '@/components/efriends/MapPreview.vue'
  import Producers from '@/components/efriends/Producers.vue'
  import Consumers from '@/components/efriends/Consumers.vue'
  import Donatees from '@/components/efriends/Donatees.vue'
  import Contributors from '@/components/efriends/Contributors.vue'
  import LinkButton from '@/components/LinkButton.vue'
  import { useStrings } from '@/composables/useStrings.ts'
  import { computed, hasInjectionContext } from 'vue'
  import Spinner from '@/components/icons/Spinner.vue'
  import { useMyCube } from '@/composables/useMyCube.js'
  import { useAppStore } from '@/stores/useAppStore'
  import { useCubeStore } from '@/stores/useCubeStore'
  import { useSettingsStore } from '@/stores/useSettingsStore'
  import { useMapStore } from '@/stores/useMapStore'
  import { storeToRefs } from 'pinia'
import { useAccountStore } from '@/stores/useAccountStore'

  export default {
    name: 'page-efriends',
    setup(){
      const appStore = useAppStore()
      const cubeStore = useCubeStore()
      const settingsStore = useSettingsStore()
      const mapStore = useMapStore()
      const { currentRouteName } = storeToRefs(appStore)
      const { t } = useStrings()
      const { hasProduction, hasConsumption } = useMyCube()
      const accountStore = useAccountStore();

      const eFriendsSingular = import.meta.env.VITE_FRIEND_SINGULAR || "eFriend";
      const eFriendsPlural = import.meta.env.VITE_FRIEND_PLURAL || "eFriends";
      const isCommunityless = computed(() => accountStore.properties.isCommunityless);

      return {
        t,
        cubeStore,
        mapStore,
        currentRouteName,
        unusedEnergyLines: computed(() => cubeStore.unusedEnergyLines),
        openConnectionsAvailable: computed(() => cubeStore.openConnectionsAvailable),
        contributors: computed(() => [
          ...cubeStore.consumers.filter(c => c.isContributor),
          ...cubeStore.producers.filter(p => p.isContributor),
        ]),
        donatees: computed(() => [
          ...cubeStore.consumers.filter(c => c.isDonatee),
          ...cubeStore.producers.filter(p => p.isDonatee),
        ]),
        referralLink: computed(() => {
          return settingsStore.contractData?.contractId
            ? `https://www.efriends.at/efriend-werden.html?referral=${encodeURIComponent(
                settingsStore.contractData.contractId
              )}`
            : null;
        }),
        friendsLoaded: computed(() => cubeStore.connectionsReceived),
        hasProduction,
        hasConsumption,
        eFriendsSingular,
        eFriendsPlural,
        isCommunityless
      }
    },
    components: {
      Spinner,
      Contributors,
      LinkButton,
      Donatees,
      ListAndMapPreview,
      Producers,
      Consumers,
    },
    mounted() {
      if (this.cubeConnectionsReceived) {
        // Wait a little before scrolling to the cube to allow the notifications
        // sidebar to close. Otherwise both animations would show at the same
        // time which doesn't feel right and might cause jank on slower devices.
        setTimeout(() => {
          this.scrollToCube()
        }, 500)
      }
    },
    computed: {
      cubeConnectionsReceived() {
        return this.cubeStore.connectionsReceived
      },
      likesGiven: function () {
        const ownCube = this.mapStore.getCubeByName(String(this.cubeStore.name))
        return ownCube ? ownCube.votingInformation?.numLikes : null
      },
      availableLikes: function () {
        return this.cubeStore.likeableCubes.length
      },
    },
    methods: {
      scrollToCube() {
        const cube = window.location.href.match(/#([pc]_[P_0-9]+)/)
        if (!cube || !cube[1]) {
          return
        }
        const domEl = document.getElementById(cube[1])
        if (domEl) {
          domEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      },
    },
    watch: {
      currentRouteName: function (to, from) {
        if (
          to?.name === 'efriends' &&
          from?.name === 'efriends' &&
          to?.hash !== from?.hash
        ) {
          this.scrollToCube()
        }
      },
    },
  }
</script>

<style lang="scss" >
  .page-efriends {
    .icon--spinner {
      animation: spin 1.8s linear 0s infinite;
      width: $spacer20;
      margin-top: 0.5rem;
      margin-left: auto;
      margin-right: auto;
      display: block;
    }
  }
</style>
<style lang="scss" scoped>
  .page-efriends {
    max-width: $content-page-width;

    .page__title {
      margin-bottom: 0;

      @media (min-width: $bp-sm) {
        margin-top: 0;
      }

      @media (min-width: $bp-md) {
        margin-bottom: $spacer025;
      }
    }

    &__top {
      margin-top: $spacer10;

      @media (min-width: $bp-sm) {
        margin-top: $spacer30;
      }

      @media (min-width: $bp-sm) {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
      }

      &-left {
        @media (min-width: $bp-sm) {
          flex-basis: 50%;
          flex-shrink: 0;
        }
      }
    }

    &__energy-lines {
      margin-bottom: $spacer10;
      margin-right: $spacer10;

      @media (min-width: $bp-md) {
        font-size: $font-size-lg;
      }

      span {
        color: $green-dark;
      }
    }

    .icon--spinner {
      margin-top: $spacer20;
    }
  }
  .producers,
  .consumers {
    margin: $spacer20 auto 0;
    max-width: 800px;

    &__title {
      .participation {
        &__factor {
          display: none;
        }
      }

      @media (max-width: $bp-ml) {
        .participation__factor {
          display: block;
        }
      }

      @media (min-width: $bp-ml) {
        .participation__factor {
          display: none;
        }
      }
    }

    &__title {
      flex-basis: 600px;
    }

    &__header {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      align-items: baseline;
      margin-top: $spacer10;
    }

    &__list {
      &-item {
        transition: all 1s ease-in-out 0s;

        &-leave-active {
          max-height: 0;
          padding-top: 0;
          padding-bottom: 0;
          overflow: hidden;
        }
      }
    }

    &__message {
      box-shadow: 2px 2px 7px rgba(0, 0, 0, 0.15);
      padding: $spacer10 $spacer30 $spacer10 $spacer20;
      border-left: 4px solid $community;
      background: $white;
      margin: $spacer20 auto;
      max-width: 800px;

      @media (min-width: $bp-ml) {
        flex-basis: 100%;
      }
    }
  }
</style>
